import { useCallback, useEffect, useState } from 'react'
import { createPortal, flushSync } from 'react-dom'
import { Loader2, Printer, QrCode } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { tablesService } from '../../../services/tablesService'
import { useSelectedPropertyId, useSelectedPropertyName } from '../../../store/propertyStore'
import { hostWarning, scanBaseUrl, tableScanUrl } from '../qrScanUrl'
import type { Table } from '../../../types/api.types'
import { AdminBanner } from './AdminBanner'
import { EmptyState } from './EmptyState'

function errorMessage(e: unknown, fallback: string): string {
  return e instanceof Error && e.message ? e.message : fallback
}

//QR size is fixed.
const QR_PIXELS = 148

interface QrCardProps {
  table: Table
  url: string
  onPrint?: () => void
}

function QrCard({ table, url, onPrint }: QrCardProps): JSX.Element {
  return (
    <div className="qr-print-card flex flex-col items-center rounded-lg border border-dashed border-gray-300 bg-white p-4 text-center">
      <QRCodeSVG value={url} level="M" size={QR_PIXELS} marginSize={1} />
      <p className="mt-3 text-[15px] font-semibold text-gray-900">Table {table.table_number}</p>
      <p className="mt-1 break-all text-[10px] leading-tight text-gray-400">{url}</p>
      {onPrint && (
        <button
          type="button"
          onClick={onPrint}
          className="mt-3 flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700"
        >
          <Printer size={13} />
          Print this one
        </button>
      )}
    </div>
  )
}

export function QrCodesTab(): JSX.Element {
  const propertyId = useSelectedPropertyId()
  const propertyName = useSelectedPropertyName()

  const [tables, setTables] = useState<Table[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // A build-time value, not a field staff can get wrong.
  const base = scanBaseUrl()
  // null = nothing to print. A Table prints one card, 'all' prints the sheet.
  const [printTarget, setPrintTarget] = useState<Table | 'all' | null>(null)

  const loadTables = useCallback(async () => {
    if (propertyId == null) {
      setTables([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      // An unknown or non-restaurant property answers 200 {"tables": []} rather
      // than a 404, so empty here is a real answer and not a failure.
      setTables(await tablesService.getAll(propertyId))
    } catch (e) {
      setError(errorMessage(e, 'Failed to load tables'))
    } finally {
      setLoading(false)
    }
  }, [propertyId])

  useEffect(() => { loadTables() }, [loadTables])

  // flushSync so the sheet is in the DOM before print() reads layout. Clearing straight after keeps the sheet out of the
  // on-screen tree between prints.
  function print(target: Table | 'all') {
    flushSync(() => setPrintTarget(target))
    window.print()
    setPrintTarget(null)
  }

  const warning = hostWarning(base)
  const printing = printTarget === 'all' ? tables : printTarget ? [printTarget] : []

  return (
    <>
      <div>
        <AdminBanner message={error} variant="error" />
        <AdminBanner message={warning} variant="warning" />

        <div className="mb-6 flex justify-end">
          <button
            type="button"
            onClick={() => print('all')}
            disabled={tables.length === 0}
            className="flex items-center justify-center gap-2 rounded-lg bg-brand-green px-4 py-2 text-[14px] font-semibold text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Printer size={16} />
            Print sheet
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 size={24} className="animate-spin text-brand-green" />
            <span className="ml-2 text-sm text-gray-500">Loading tables...</span>
          </div>
        ) : tables.length === 0 ? (
          <EmptyState
            icon={QrCode}
            message="No tables yet"
            hint="Add a table for this property first — its QR code appears here as soon as you do."
          />
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {tables.map((t) => (
              <QrCard
                key={t.id}
                table={t}
                url={tableScanUrl(base, t.property_id, t.table_number)}
                onPrint={() => print(t)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Portalled to <body> on purpose: the sidebar and top bar are ancestors
          of this tab, and an ancestor cannot be hidden while a descendant stays
          visible. As a sibling of #root, one print rule isolates the sheet.
          Kept out of the page's spacing stack for the usual overlay reason. */}
      {printing.length > 0 &&
        createPortal(
          <div className="qr-print-sheet">
            <h1 className="mb-4 text-[16px] font-semibold text-black">
              {propertyName ?? 'Table'} — scan to order
            </h1>
            <div className="grid grid-cols-3 gap-4">
              {printing.map((t) => (
                <QrCard
                  key={t.id}
                  table={t}
                  url={tableScanUrl(base, t.property_id, t.table_number)}
                />
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}
